/**
 * ImportSummary Component
 * Shows balance summary and import statistics before final import
 */
import Card from '@/components/ui/Card';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { BalanceInfo } from '@/types/document';

export interface ImportSummaryProps {
  balanceInfo: BalanceInfo;
  selectedCount: number;
  totalCount: number;
}

export function ImportSummary({ balanceInfo, selectedCount, totalCount }: ImportSummaryProps) {
  const hasBalanceData =
    balanceInfo.openingBalance !== null ||
    balanceInfo.closingBalance !== null ||
    balanceInfo.statementClosing !== null;

  return (
    <Card className="bg-gray-50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Selection summary */}
        <div>
          <h3 className="text-sm font-medium text-gray-900">Import Summary</h3>
          <p className="mt-1 text-sm text-gray-600">
            {selectedCount} of {totalCount} transactions selected
          </p>
        </div>

        {/* Balance info */}
        {hasBalanceData && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {balanceInfo.openingBalance !== null && (
              <div>
                <span className="text-gray-500">Opening:</span>{' '}
                <span className="font-medium text-gray-900">
                  {formatCurrency(balanceInfo.openingBalance)}
                </span>
              </div>
            )}

            {balanceInfo.netChange !== 0 && (
              <div>
                <span className="text-gray-500">Net Change:</span>{' '}
                <span
                  className={cn(
                    'font-medium',
                    balanceInfo.netChange < 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {formatCurrency(balanceInfo.netChange)}
                </span>
              </div>
            )}

            {balanceInfo.closingBalance !== null && (
              <div>
                <span className="text-gray-500">Closing:</span>{' '}
                <span className="font-medium text-gray-900">
                  {formatCurrency(balanceInfo.closingBalance)}
                </span>
              </div>
            )}

            {balanceInfo.statementClosing !== null && balanceInfo.closingBalance !== null && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Statement:</span>{' '}
                <span className="font-medium text-gray-900">
                  {formatCurrency(balanceInfo.statementClosing)}
                </span>
                <BalanceMatchIndicator matches={balanceInfo.balanceMatches} />
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Balance match indicator
 */
function BalanceMatchIndicator({ matches }: { matches: boolean }) {
  if (matches) {
    return (
      <span
        className="inline-flex items-center text-green-600"
        title="Balance matches statement"
      >
        <CheckIcon className="w-4 h-4" />
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center text-yellow-600"
      title="Balance does not match statement"
    >
      <WarningIcon className="w-4 h-4" />
    </span>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

export default ImportSummary;
